/*
 * <grind-gate> — obbliga la scelta del formato prima dell'aggiunta al carrello.
 *
 * All'atterraggio su una scheda prodotto senza variante esplicita nell'URL,
 * nessun formato risulta preselezionato: il bottone di acquisto resta bloccato
 * finche' l'utente non sceglie. Serve a evitare gli ordini sbagliati dovuti alla
 * preselezione implicita di Shopify, che sceglieva sempre la prima variante.
 *
 * Principio: il gate BLOCCA soltanto. Non ripristina nulla in anticipo.
 * Alla scelta, product-info.js ri-renderizza la sezione lato server e rimette a
 * posto prezzo, profilo di tostatura, id variante ed etichetta del bottone con i
 * dati della variante giusta. Anticipare quel ripristino mostrava per un istante
 * i dati della PRIMA variante (prezzo e selettore tostatura che lampeggiavano) e
 * lasciava una finestra in cui il form conteneva ancora l'id precedente.
 */
if (!customElements.get('grind-gate')) {
  // Sopravvive alla sostituzione del DOM operata da HTMLUpdateUtility.viewTransition.
  // E' legato al pathname, cosi' un product-swap (altro prodotto) rialza il gate.
  let releasedForPath = null;

  customElements.define(
    'grind-gate',
    class GrindGate extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest('product-info');
        if (!this.productInfo) return;

        // Nei contesti senza aggiornamento URL (quick add, prodotto in vetrina)
        // il gate non avrebbe modo di sapere di essere gia' stato sciolto.
        if (this.productInfo.dataset.updateUrl === 'false') return;

        if (releasedForPath === window.location.pathname) return;
        if (new URLSearchParams(window.location.search).has('variant')) return;

        this.inputs = Array.from(this.querySelectorAll('input[type="radio"]'));
        if (this.inputs.length === 0) return;

        this.clearSelection();
        this.lock();

        this.addEventListener('change', this.onChoice.bind(this), { once: true });
      }

      clearSelection() {
        this.inputs.forEach((input) => {
          input.checked = false;
          input.removeAttribute('checked');
        });

        const readout = this.querySelector('[data-selected-value]');
        if (readout) readout.textContent = '';
      }

      get submitButton() {
        return this.productInfo.querySelector('.product-form__submit');
      }

      get variantIdInput() {
        return this.productInfo.querySelector('input[name="id"].product-variant-id');
      }

      lock() {
        const button = this.submitButton;
        if (button) {
          const label = button.querySelector('span');
          if (label && this.dataset.prompt) label.textContent = this.dataset.prompt;
          button.setAttribute('disabled', 'disabled');
        }

        // Il checkout rapido bypasserebbe il form, quindi va nascosto.
        this.productInfo.querySelectorAll('.shopify-payment-button').forEach((element) => {
          element.hidden = true;
        });

        // Doppia sicurezza: senza id valido il form non puo' aggiungere nulla.
        const idInput = this.variantIdInput;
        if (idInput) idInput.disabled = true;

        // Senza formato scelto mostriamo "Da <prezzo minimo>": il prezzo della
        // prima variante sarebbe arbitrario, e qui e' pure il piu' alto.
        this.showFromPrice();

        // Il profilo di tostatura descrive una variante specifica.
        this.productInfo.querySelector('[data-grind-roast]')?.classList.add('hidden');

        this.setAttribute('data-locked', '');
      }

      showFromPrice() {
        const real = this.productInfo.querySelector('[data-grind-price-real]');
        const from = this.productInfo.querySelector('[data-grind-price-from]');
        if (!real || !from) return;

        real.hidden = true;
        from.hidden = false;
      }

      onChoice() {
        releasedForPath = window.location.pathname;

        // Unica cosa immediata: via la nota di obbligatorieta', che non e' piu'
        // vera. Il feedback sulla scelta lo da' gia' la card, in CSS.
        this.removeAttribute('data-locked');

        // Lo sblocco vive in una closure, non su questo elemento: <grind-gate>
        // sta dentro <variant-selects>, che product-info.js sostituisce PRIMA di
        // pubblicare variantChange. Legarlo all'istanza funzionava soltanto
        // perche' HTMLUpdateUtility.viewTransition tiene in vita il vecchio nodo
        // per 500ms (assets/global.js) — una dipendenza accidentale che, se quel
        // ritardo cambiasse, lascerebbe l'input id disabilitato e il prodotto
        // non acquistabile senza alcun errore visibile.
        const productInfo = this.productInfo;
        const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => {
          unsubscribe();

          // Prezzo, tostatura, etichetta e stato del bottone li ha gia' rimessi
          // product-info.js dal markup di sezione aggiornato. Qui restano solo
          // le due leve che nessun altro tocca. Rileggiamo dal DOM: i nodi
          // catturati al lock potrebbero essere stati sostituiti.
          const idInput = productInfo.querySelector('input[name="id"].product-variant-id');
          if (idInput) idInput.disabled = false;

          productInfo.querySelectorAll('.shopify-payment-button').forEach((element) => {
            element.hidden = false;
          });
        });
      }
    }
  );
}
